import React from "react";
import { Loader, RefreshCw } from "lucide-react";
import { useCryptoNetworks, useRunCryptoNetworkCheck, useToggleCryptoNetwork } from "../hooks/useCryptoNetworks";

const statusStyles = {
  ok: "bg-emerald-100 text-emerald-800",
  unavailable: "bg-red-100 text-red-700",
  unknown: "bg-gray-100 text-gray-500",
};

function StatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusStyles[status] ?? statusStyles.unknown}`}>
      {status}
    </span>
  );
}

// Wallet creation is refused on a paused network (the message tells the user
// it is temporarily unavailable). The status comes from a check that runs
// every 15 minutes: it shows whether the provider still recognises the
// network and can price a withdrawal on it, not that the chain itself is up,
// and an "unavailable" status does not block creation by itself - pausing does.
function CryptoNetworksTable() {
  const { data, isLoading, isError, error } = useCryptoNetworks();
  const { mutate: runCheck, isPending: checking } = useRunCryptoNetworkCheck();
  const { mutate: toggle, isPending: toggling, variables } = useToggleCryptoNetwork();

  if (isLoading) {
    return (
      <div className="flex gap-2 items-center text-sm text-gray-500">
        <Loader className="w-5 h-5 animate-spin" /> Loading networks...
      </div>
    );
  }
  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">Error: {error.message}</p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 text-sm text-gray-600 bg-gray-50 rounded-xl border border-gray-200">
        Pause a network to stop new wallets being created on it (for example if BTC addresses are failing).
        Existing wallets are untouched. The status is checked every 15 minutes and shows whether the provider
        still recognises the network and can price a withdrawal on it.
      </div>

      <button
        onClick={() => runCheck()}
        disabled={checking}
        className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
      >
        {checking ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        Check now
      </button>

      <div className="overflow-hidden rounded border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                {["Currency", "Network", "Wallet creation", "Status", "Last checked", "Last error", ""].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:px-6"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-sm text-center text-gray-500">
                    No networks found
                  </td>
                </tr>
              )}
              {data.map((n) => (
                <tr key={n.uuid} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700 uppercase md:px-6">{n.currency}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 md:px-6">
                    {n.name} <span className="text-gray-400">({n.symbol})</span>
                  </td>
                  <td className="px-4 py-3 text-sm md:px-6">
                    {n.isEnabled ? (
                      <span className="font-medium text-emerald-700">On</span>
                    ) : (
                      <span className="font-medium text-red-600">Paused</span>
                    )}
                  </td>
                  <td className="px-4 py-3 md:px-6">
                    <StatusPill status={n.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 md:px-6">
                    {n.lastCheckedAt ? new Date(n.lastCheckedAt).toLocaleString() : "-"}
                  </td>
                  <td className="px-4 py-3 max-w-xs text-sm text-gray-500 truncate md:px-6" title={n.lastError ?? ""}>
                    {n.lastError ?? "-"}
                  </td>
                  <td className="px-4 py-3 text-right md:px-6">
                    <button
                      onClick={() => toggle({ uuid: n.uuid, isEnabled: !n.isEnabled })}
                      disabled={toggling && variables?.uuid === n.uuid}
                      className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50"
                    >
                      {n.isEnabled ? "Pause" : "Resume"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CryptoNetworksTable;
